import { Injectable } from "@angular/core";
import { doc, Firestore, getDoc, onSnapshot } from "@angular/fire/firestore";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { isMobile, UtilityService } from "@core/utility.service";
import { Popover } from "@interfaces/popover.enum";
import * as moment from "moment";
import { delay, Observable, of, Subscription, take } from "rxjs";
import { CommonVariable } from "../app.constants";
import { AddHomeScreenComponent } from "../components/add-home-screen/add-home-screen.component";
import { QuickAccessComponent } from "../components/quick-access/quick-access.component";
import { GamePreviewComponent } from "../pages/games-feed-sample/game-preview/game-preview.component";
import { GamesFeedSampleService } from "../pages/games-feed-sample/games-feed-sample.service";
import { GamesRepositoryService } from "../pages/games/games-repository.service";
import { GameCelebrationService } from "../pages/games/services/game-celebration.service";
import { GameDatasService } from "../pages/games/services/game-data.service";

@Injectable({
  providedIn: "root",
})
export class GameSessionService {
  private static readonly GAMES_RETURN_URL_KEY = "games_feed_return_url";
  private static readonly GAME_NEW_PATH = "/mainTabs/tabs/game-new";
  private static readonly GAME_PATH = "/mainTabs/tabs/game";

  gameMiniSubscription: Subscription;
  gamePopupSubscription: Subscription;
  preferredCurrency = CommonVariable.preferredCurrency;

  constructor(
    private router: Router,
    private gamesFeedService: GamesFeedSampleService,
    private authService: AuthService,
    private matDialog: MatDialog,
    private gamesRepositoryService: GamesRepositoryService,
    private window: Window,
    private bottomSheet: MatBottomSheet,
    private firestore: Firestore,
    private gameCelebrationService : GameCelebrationService,
    private gameDatasService: GameDatasService
  ) { }

  /** Persist which games tab the user was on before play (game-new vs classic). */
  public rememberGamesFeedOrigin(url?: string): void {
    const returnUrl = this.resolveGamesFeedReturnUrl(url);
    try {
      sessionStorage.setItem(GameSessionService.GAMES_RETURN_URL_KEY, returnUrl);
    } catch {
      // sessionStorage may be unavailable; in-memory fallback not required
    }
  }

  /** Where to land after a game session ends. */
  public getGamesFeedReturnUrl(): string {
    try {
      const stored = sessionStorage.getItem(GameSessionService.GAMES_RETURN_URL_KEY);
      if (stored === GameSessionService.GAME_NEW_PATH || stored === GameSessionService.GAME_PATH) {
        return stored;
      }
    } catch {
      // ignore and fall through to live URL resolution
    }
    return this.resolveGamesFeedReturnUrl();
  }

  private resolveGamesFeedReturnUrl(url?: string): string {
    const candidate =
      url ||
      this.router.url ||
      (typeof this.window !== "undefined" ? this.window.location?.pathname : "") ||
      "";
    return candidate.includes("game-new")
      ? GameSessionService.GAME_NEW_PATH
      : GameSessionService.GAME_PATH;
  }

  public getGameSessionChanges(userId: string, gamePlaySessionId: string): Observable<any> {
    const docRef = doc(this.firestore, `users/${userId}/game_play_session/${gamePlaySessionId}`);
    return new Observable<any>((observer) => {
      const unsubscribe = onSnapshot(docRef, (snapshot) => {
        if (snapshot.exists()) {
          observer.next(snapshot.data());
        } else {
          observer.next(null); // Or handle as needed
        }
      }, (error) => {
        observer.error(error);
      });

      // Cleanup subscription
      return () => unsubscribe();
    });
  }

  // To redirect user to specific route path based on given app url
  public inGamePromotionalRedirection(redirectionUrl: string): void {
    try {
      let redirectionRoute: string;

      // Check if the incoming URL is absolute or relative
      if (redirectionUrl.startsWith("http://") || redirectionUrl.startsWith("https://")) {
        const urlObj = new URL(redirectionUrl);
        redirectionRoute = `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
      } else {
        // Treat it as a relative path
        redirectionRoute = redirectionUrl;
        console.info("the redirectional route is ", redirectionRoute);
      }
      this.router.navigateByUrl(redirectionRoute).then(result => {
        console.info("Navigation result:", result);
      }).catch(err => {
        console.info("%cNavigation error:", "color: red;", err);

      });
    } catch (error) {
      this.router.navigateByUrl(this.getGamesFeedReturnUrl());
      return error;
    }
  }

  public handleFinishSuggestionCase(data, dialogRef) {
    const url = data?.redirect_url;
    const fragment = Number(url.split("#")[1]) as any;
    getDoc(doc(
      this.firestore,
      `game_mini_doc/${fragment}` // Directly specify the document path
    )
    ).then((docSnap) => {
      if (docSnap.exists()) {
        const filterGameData = docSnap.data();
        const gameCategory = filterGameData?.category.toLowerCase().replace(/\s+/g, "_");
        this.gamePopupSubscription = this.gamesFeedService
          .getGamePopupData(gameCategory, fragment)
          .subscribe((gameData) => {
            const inputData = {
              myString: gameData,
              currency: this.preferredCurrency,
            };
            this.authService
              .getDvResolvedJson(inputData)
              .then((gameDataDvResolved: Data) => {
                this.gamesRepositoryService?.setCurrentGameOrientationStatus(gameData?.orientation);
                dialogRef.close();
                this.gamePopupSubscription?.unsubscribe();
                if (!this.gamesFeedService?.isGamePopupVisible) {
                  this.matDialog.open(GamePreviewComponent, {
                    panelClass: "gamePreview-size",
                    backdropClass: "ongoingWarning-back-drop-color",
                    data: gameDataDvResolved?.data,
                  });
                }
              })
              .catch(() => {
                this.authService.getConstantsMsg("1013").then((translatedMsg) => {
                  const defaultMsg = {
                    message: {
                      line_1: "Something went wrong (user)",
                      line_2: "",
                    },
                    style: {
                      icon: "tick circle error.svg",
                      type: CommonVariable.error,
                    },
                  };
                  this.authService.showToastMessage(translatedMsg, defaultMsg);
                });
              });
          });
      }
    }).catch((error) => {
      console.error("Error fetching game mini document:", error);
    });
  }

  public handleUserChoiceCase(data) {
    this.gameDatasService.updateGamePlayLastSession(data?.parent_game_id);
    this.authService
      .getConstantsMsg(Popover.Id_1006)
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "You've ended your game session successfully",
            line_2: "",
          },
          style: {
            icon: "tick circle success.png",
            type: "Success",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((error) => {
        return error;
      });
  }

  public handleToastMessageCase(data, playGameStatus, dialogRef) {
    // This method has been used to display toast message after finished game and checklist steps complete
    const delayedObservable = of(null).pipe(delay(8000), take(1));
    delayedObservable.subscribe(() => {
      if (playGameStatus === false) {
        this.toastMessage();
      }
    });
    if (data?.current_game_token && data?.game_play_token[data?.current_game_token]?.play_status === "WIN") {
      //Displayed token animation
      this.gameCelebrationService?.checkTokenAnimation();
    }
    setTimeout(() => {
      dialogRef.close();
      if (isMobile.iOS()) {
        this.checkIfUserIsOnStandAlone();
      } else if (isMobile.Android()) {
        this.addHomeScreen();
      }
      if (data?.close_reason === "USER_CHOICE") {
        this.handleUserChoiceCase(data);
      }
      if (!data || !data?.game_play_token || !data?.current_game_token) return;
      if (data.error_code != "") {
        const errorMsg = UtilityService.processErrorMessage(data.error_code);
        //this.authService.showToast(errorMsg ? errorMsg : "Something went wrong!", "Error", "");
        const translatedMsg = {
          message: {
            line_1: errorMsg,
            line_2: "",
          },
          style: {
            duration: 2000,
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        const defaultMsg = {
          message: {
            line_1: "Something went wrong!",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      } else if (data?.game_play_token[data?.current_game_token]?.play_status === "WIN") {
        /* const msg1 = this.translatedToastMsg?.["1008"]?.[this.selectedLanguage];
                this.authService.showToast(msg1 ? msg1 : "Your prize has been added to your prize unlocker", "Success", ""); */
        this.authService
          .getConstantsMsg(Popover.Id_1008)
          .then((translatedMsg) => {
            const defaultMsg = {
              message: {
                line_1: "Your prize has been added to your account",
                line_2: "",
              },
              style: {
                icon: "tick circle success.png",
                type: "Success",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          })
          .catch((error) => {
            return error;
          });
      }
      this.gameDatasService.updateGamePlayLastSession(data?.parent_game_id);
    }, 3000);
  }

  toastMessage() {
    this.authService
      .getConstantsMsg("1118")
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "Completed action",
            line_2: "Checklist steps complete!",
          },
          style: {
            icon: "tick circle success.png",
            type: "Success",
          },
        };
        // This method has been used to set toast message
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((error) => {
        return error;
      });
  }

  // below this method is used to check if the user is using IOS
  checkIfUserIsOnStandAlone() {
    const gamePopupShown = localStorage.getItem(Popover.gamePopUpShown) === Popover.isTrue;
    if (UtilityService.isAppInstalledOnHomePage() || gamePopupShown) {
      return;
    } else {
      if (isMobile.iOS()) {
        if (
          navigator.vendor &&
          navigator.vendor.indexOf("Apple") > -1 &&
          navigator.userAgent &&
          navigator.userAgent.indexOf("CriOS") == -1 &&
          navigator.userAgent.indexOf("FxiOS") == -1
        ) {
          this.authService?.userDetails$?.pipe(take(1))?.subscribe((data: Data) => {
            if (!data?.installed_last_auto_login_date) {
              setTimeout(() => {
                this.quickAccess();
              }, 5000);
              return;
            }
            // const { format } = require("date-fns");
            if (!data || !data?.installed_last_auto_login_date) return;
            // let today = format(new Date());
            // let past = format(new Date(data.installed_last_auto_login_date.toDate()));
            let today = moment();
            let past = moment(data?.installed_last_auto_login_date.toDate()).format();
            let gap = today.diff(past, "days");

            if (gap > 14) {
              setTimeout(() => {
                this.quickAccess();
              }, 5000);
            }
            return;
          });
        } else {
          /* const msg = this.translatedToastMsg?.["1043"]?.[this.selectedLanguage];
            this.authService.showToast(msg ? msg : "Code Error", "Success", ""); */
          this.authService
            .getConstantsMsg(Popover.Id_1043)
            .then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "The app works best on Safari browser",
                  line_2: "",
                },
                style: {
                  icon: "tick circle success.png",
                  type: "Success",
                },
              };
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            })
            .catch((err) => {
              return err;
            });
        }
      }
      localStorage.setItem(Popover.gamePopUpShown, Popover.isTrue);
    }
  }

  // The addHomeScreen method is used to  add-home-screen-bottomSheet on homeScreen
  addHomeScreen() {
    const gamePopupShown = localStorage.getItem(Popover.gamePopUpShown) === Popover.isTrue;
    if (UtilityService.isAppInstalledOnHomePage() || gamePopupShown) {
      return;
    }
    if (this.window["customEvent"]) {
      const addHomeScreen = this.bottomSheet.open(AddHomeScreenComponent, {
        panelClass: "add-home-popup-bottomSheet",
      });

      addHomeScreen?.afterDismissed()?.pipe(take(1))?.subscribe((data) => {
        if (data) {
          this.window["customEvent"].prompt();
          this.window["customEvent"].userChoice.then();
        }
      });
      localStorage.setItem(Popover.gamePopUpShown, Popover.isTrue);
    }
  }

  quickAccess() {
    this.bottomSheet.open(QuickAccessComponent, {
      panelClass: "quick-popup-bottomSheet",
    });
  }
}
